<template>
  <section class="magazine-list">
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <b-input
              v-model="search"
              :placeholder="$t('attributes.search')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.magazine') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="isLoading"
      :mobile-cards="false"
      hoverable
      narrowed
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :current-page="parseInt(page)"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="publish"
        :label="$t('attributes.activation_status')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <b-button
          :type="props.row.publish ? 'is-success' : 'is-warning is-light'"
          rounded
          size="is-small"
          @click.prevent="onChangeStatus(props.row)"
        >
          {{
            props.row.publish
              ? $t('attributes.magazine_on')
              : $t('attributes.magazine_off')
          }}
        </b-button>
      </b-table-column>

      <b-table-column
        field="id"
        :visible="false"
        :label="$t('attributes.id')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="code"
        :label="$t('attributes.code')"
        width="75"
        sortable
        v-slot="props"
      >
        {{ props.row.code }}
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        width="200"
        sortable
        v-slot="props"
      >
        {{ props.row.name }}
      </b-table-column>

      <b-table-column
        field="date"
        :label="$t('attributes.magazine_date')"
        width="200"
        sortable
        v-slot="props"
      >
        {{ props.row.date }}
      </b-table-column>

      <b-table-column
        field="pages"
        :label="$t('attributes.magazine_pages')"
        width="75"
        sortable
        numeric
        v-slot="props"
      >
        {{ props.row.pages }}
      </b-table-column>

      <b-table-column
        :label="$t('attributes.magazine_cover')"
        centered
        v-slot="props"
      >
        <img
          :src="cover(props.row)"
          :alt="$t('attributes.cover')"
          style="width: 80px; margin: auto;"
        />
      </b-table-column>

      <b-table-column
        :label="$t('attributes.magazine_file')"
        centered
        v-slot="props"
      >
        <a :href="download(props.row)" target="_blank">下載</a>
      </b-table-column>

      <b-table-column
        field="file_path"
        :visible="false"
        :label="$t('attributes.magazine_file_path')"
        v-slot="props"
      >
        {{ props.row.file_path }}
      </b-table-column>

      <b-table-column
        field="file_name"
        :visible="false"
        :label="$t('attributes.magazine_file_name')"
        v-slot="props"
      >
        {{ props.row.file_name }}
      </b-table-column>

      <b-table-column
        field="link_list"
        :label="$t('attributes.magazine_link_list')"
        v-slot="props"
        cell-class="link-cell"
      >
        <ul>
          <li
            v-for="(link, index) in props.row.link_list"
            :key="index"
            class="magazine-link is-flex is-justify-content-space-between is-size-7"
          >
            <span>{{ link.title }}</span>
            <span>{{ link.page }}</span>
          </li>
        </ul>
      </b-table-column>

      <b-table-column
        field="content_list"
        :label="$t('attributes.magazine_content_list')"
        v-slot="props"
        cell-class="link-cell"
      >
        <ul>
          <li
            v-for="(content, index) in props.row.content_list"
            :key="index"
            class="magazine-link is-flex is-justify-content-space-between is-size-7"
          >
            <span>{{ content.title }}</span>
            <span>{{ content.page }}</span>
          </li>
        </ul>
      </b-table-column>

      <b-table-column
        field="created_at"
        :label="$t('attributes.create_date')"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ formatDateTime(props.row.created_at) }}
      </b-table-column>

      <b-table-column :width="70" label="功能" centered v-slot="props">
        <div class="buttons is-centered">
          <b-button type="is-primary" @click.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['x']"
      has-modal-card
    >
      <magazine-form :magazine="selectedItem" @send="onSend" />
    </b-modal>

    <b-loading
      :active.sync="sending"
      :is-full-page="true"
      :can-cancel="false"
    ></b-loading>
  </section>
</template>

<script>
import { MAGAZINES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import debounce from 'lodash/debounce'
import MagazineForm from './MagazineForm'

export default {
  name: 'MagazineList',
  components: { MagazineForm },

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      filterStatus: '',
      search: this.query.search || '',
      sortField: this.query.sort || 'date',
      sortOrder: this.query.order || 'desc',
      defaultSortOrder: 'desc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      isFormModalActive: false,
      sending: false
    }
  },
  watch: {
    query(query) {
      this.getQuery(query)
      this.loadItems()
    }
  },
  computed: {
    isLoading() {
      return this.loadingItems
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    async loadItems() {
      try {
        const api = `${MAGAZINES_API}`
        const params = {
          search: this.search,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('magazines', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.search = query.search || ''
      this.sortField = query.sort || 'date'
      this.sortOrder = query.order || 'desc'
      this.page = query.page || 1
      this.perPage = query.page_size || 10
    },
    updateQuery() {
      const query = {
        search: this.search,
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilterStatus() {
      this.page = 1
      this.updateQuery()
    },
    onFilter: debounce(function() {
      this.page = 1
      this.updateQuery()
    }, 500),
    onPageChange(page) {
      this.page = page
      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.updateQuery()
    },
    onAdd() {
      this.selectedItem = {
        id: null,
        code: null,
        publish: false,
        date: null,
        name: null,
        pages: null,
        file_path: null,
        file_name: null,
        link_list: [],
        content_list: []
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.selectedItem = item
      this.isFormModalActive = true
    },
    onSend(item) {
      if (this.selectedItem.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        this.sending = true
        await this.axios.post(MAGAZINES_API, item)

        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${MAGAZINES_API}/${item.id}`

        this.sending = true
        await this.axios.put(api, item)
        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async onChangeStatus(item) {
      if (item.publish) {
        return
      }

      try {
        const api = `${MAGAZINES_API}/${item.id}/publish`

        this.sending = true
        await this.axios.put(api, { publish: !item.publish })
        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    cover(magazine) {
      const file = `${magazine.file_path}/${magazine.file_name}.jpg`
      return this.MagazineUrl(file)
    },
    download(magazine) {
      const file = `${magazine.file_path}/${magazine.file_name}.pdf`
      return this.MagazineUrl(file)
    }
  }
}
</script>

<style lang="scss">
.magazine-list {
  .magazine-link {
    color: gray;
    border-bottom: transparent 1px solid;

    &:hover {
      color: darkblue;
      font-weight: 500;
      border-bottom: gray 1px solid;
    }
  }

  .link-cell {
    vertical-align: top;
  }
}
</style>
